import type { AdminVehicle } from '@ecowheels/types';
import {
  ELECTRIFIED_FUEL_TYPES,
  FuelType,
  Transmission,
  VehicleStatus,
} from '@ecowheels/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ConfirmButton,
  Field,
  Input,
  PageHeader,
  QueryState,
  Select,
  Textarea,
} from '@/components/ui';
import {
  BODY_LABELS,
  DRIVETRAIN_LABELS,
  FUEL_LABELS,
  STATUS_HINTS,
  STATUS_LABELS,
  STATUS_TONE,
  TRANSMISSION_LABELS,
  options,
  vehicleTitle,
} from '@/lib/vehicle-labels';
import { VehiclePhotos } from '@/components/vehicle-photos';

/**
 * Every field is held as a string, exactly as the inputs produce it, and sent
 * that way. The API's `normaliseVehicleUpsert` is the single place that decides
 * what '' means (null — "not known", never 0) and parses numbers, so doing it
 * again here would be a second set of rules to keep in sync with the first.
 */
type FormState = {
  make: string;
  model: string;
  variant: string;
  year: string;
  mileageKm: string;
  priceHuf: string;
  vatDeductible: boolean;
  fuelType: FuelType;
  transmission: Transmission;
  bodyType: string;
  drivetrain: string;
  powerKw: string;
  powerHp: string;
  colour: string;
  doors: string;
  seats: string;
  firstRegistration: string;
  motExpiry: string;
  batteryCapacityKwh: string;
  rangeKm: string;
  batteryHealthPercent: string;
  chargingAcKw: string;
  chargingDcKw: string;
  cableSet: string;
  equipment: string;
  descriptionHu: string;
  descriptionEn: string;
  status: VehicleStatus;
};

const EMPTY: FormState = {
  make: '',
  model: '',
  variant: '',
  year: '',
  mileageKm: '',
  priceHuf: '',
  vatDeductible: false,
  // Most of the stock is electric, so these two defaults are right more often
  // than not — and both are required, so a default saves two clicks per car.
  fuelType: FuelType.ELECTRIC,
  transmission: Transmission.AUTOMATIC,
  bodyType: '',
  drivetrain: '',
  powerKw: '',
  powerHp: '',
  colour: '',
  doors: '',
  seats: '',
  firstRegistration: '',
  motExpiry: '',
  batteryCapacityKwh: '',
  rangeKm: '',
  batteryHealthPercent: '',
  chargingAcKw: '',
  chargingDcKw: '',
  cableSet: '',
  equipment: '',
  descriptionHu: '',
  descriptionEn: '',
  // New cars start invisible, so a half-filled listing is never briefly public.
  status: VehicleStatus.DRAFT,
};

/** API shape → form strings. `null` becomes '', which round-trips back to null. */
function toForm(vehicle: AdminVehicle): FormState {
  const text = (value: string | number | null) => (value === null ? '' : String(value));
  return {
    make: vehicle.make,
    model: vehicle.model,
    variant: text(vehicle.variant),
    year: text(vehicle.year),
    mileageKm: text(vehicle.mileageKm),
    priceHuf: text(vehicle.priceHuf),
    vatDeductible: vehicle.vatDeductible,
    fuelType: vehicle.fuelType,
    transmission: vehicle.transmission,
    bodyType: text(vehicle.bodyType),
    drivetrain: text(vehicle.drivetrain),
    powerKw: text(vehicle.powerKw),
    powerHp: text(vehicle.powerHp),
    colour: text(vehicle.colour),
    doors: text(vehicle.doors),
    seats: text(vehicle.seats),
    firstRegistration: text(vehicle.firstRegistration),
    motExpiry: text(vehicle.motExpiry),
    batteryCapacityKwh: text(vehicle.batteryCapacityKwh),
    rangeKm: text(vehicle.rangeKm),
    batteryHealthPercent: text(vehicle.batteryHealthPercent),
    chargingAcKw: text(vehicle.chargingAcKw),
    chargingDcKw: text(vehicle.chargingDcKw),
    cableSet: text(vehicle.cableSet),
    // One per line: a textarea is the right control for a short free list, and
    // it's what the API splits back into an array.
    equipment: (vehicle.equipment ?? []).join('\n'),
    descriptionHu: vehicle.descriptionHu,
    descriptionEn: text(vehicle.descriptionEn),
    status: vehicle.status,
  };
}

function toPayload(form: FormState) {
  return {
    ...form,
    equipment: form.equipment
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean),
  };
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="p-4">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-brand">{title}</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">{children}</div>
    </Card>
  );
}

export function VehicleEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === undefined;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['vehicles', id],
    queryFn: () => api.get<AdminVehicle>(`/admin/vehicles/${id}`),
    enabled: !isNew,
  });

  useEffect(() => {
    if (data) setForm(toForm(data));
  }, [data]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const save = useMutation({
    mutationFn: (payload: ReturnType<typeof toPayload>) =>
      isNew
        ? api.post<AdminVehicle>('/admin/vehicles', payload)
        : api.put<AdminVehicle>(`/admin/vehicles/${id}`, payload),
    onSuccess: (vehicle) => {
      setError(null);
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      // A new car goes straight to its own page: photos attach to an id, so
      // there is nowhere to upload them until it exists.
      if (isNew) navigate(`/vehicles/${vehicle.id}`, { replace: true });
    },
    // The API's messages name the offending field ("year: must be at least
    // 1900"), which is more use than a generic failure line.
    onError: (e: ApiError) => setError(e.message),
  });

  // The EV block is irrelevant on a diesel, and leaving it visible invites
  // filling in a battery size for a car that hasn't got one.
  const showBattery = ELECTRIFIED_FUEL_TYPES.includes(form.fuelType);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    save.mutate(toPayload(form));
  }

  return (
    <>
      <PageHeader
        title={isNew ? 'Új jármű' : vehicleTitle(data ?? { year: 0, make: '', model: '' }) || 'Jármű'}
        action={
          <div className="flex items-center gap-2">
            {!isNew && data && (
              <Badge tone={STATUS_TONE[data.status]}>{STATUS_LABELS[data.status]}</Badge>
            )}
            <Button variant="ghost" onClick={() => navigate('/vehicles')}>
              Vissza
            </Button>
          </div>
        }
      />

      <QueryState isLoading={!isNew && isLoading} error={loadError}>
        <form onSubmit={submit} className="space-y-4">
          <Section title="Alapadatok">
            <Field label="Márka" htmlFor="make" required>
              <Input
                id="make"
                value={form.make}
                onChange={(e) => set('make', e.target.value)}
                required
              />
            </Field>
            <Field label="Modell" htmlFor="model" required>
              <Input
                id="model"
                value={form.model}
                onChange={(e) => set('model', e.target.value)}
                required
              />
            </Field>
            <Field
              label="Kivitel / felszereltség"
              htmlFor="variant"
              hint="Pl. „40 kWh Tekna”. Megjelenik a hirdetés címében."
            >
              <Input
                id="variant"
                value={form.variant}
                onChange={(e) => set('variant', e.target.value)}
              />
            </Field>
            <Field label="Évjárat" htmlFor="year" required>
              <Input
                id="year"
                type="number"
                inputMode="numeric"
                value={form.year}
                onChange={(e) => set('year', e.target.value)}
                required
              />
            </Field>
            <Field label="Futásteljesítmény (km)" htmlFor="mileageKm" required>
              <Input
                id="mileageKm"
                type="number"
                inputMode="numeric"
                value={form.mileageKm}
                onChange={(e) => set('mileageKm', e.target.value)}
                required
              />
            </Field>
            <Field
              label="Ár (Ft)"
              htmlFor="priceHuf"
              hint="Üresen hagyva „Ár kérésre” jelenik meg."
            >
              <Input
                id="priceHuf"
                type="number"
                inputMode="numeric"
                value={form.priceHuf}
                onChange={(e) => set('priceHuf', e.target.value)}
              />
            </Field>
            <Field label="ÁFA" htmlFor="vatDeductible">
              <label className="flex items-center gap-2 text-sm text-white/70">
                <input
                  id="vatDeductible"
                  type="checkbox"
                  checked={form.vatDeductible}
                  onChange={(e) => set('vatDeductible', e.target.checked)}
                  className="h-4 w-4 accent-brand"
                />
                ÁFÁ-s számla adható
              </label>
            </Field>
            <Field label="Szín" htmlFor="colour">
              <Input
                id="colour"
                value={form.colour}
                onChange={(e) => set('colour', e.target.value)}
              />
            </Field>
          </Section>

          <Section title="Műszaki adatok">
            <Field label="Hajtás / üzemanyag" htmlFor="fuelType" required>
              <Select
                id="fuelType"
                value={form.fuelType}
                onChange={(e) => set('fuelType', e.target.value as FuelType)}
              >
                {options(FUEL_LABELS).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Sebességváltó" htmlFor="transmission" required>
              <Select
                id="transmission"
                value={form.transmission}
                onChange={(e) => set('transmission', e.target.value as Transmission)}
              >
                {options(TRANSMISSION_LABELS).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Kivitel (karosszéria)" htmlFor="bodyType">
              <Select
                id="bodyType"
                value={form.bodyType}
                onChange={(e) => set('bodyType', e.target.value)}
              >
                <option value="">Nincs megadva</option>
                {options(BODY_LABELS).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Meghajtás" htmlFor="drivetrain">
              <Select
                id="drivetrain"
                value={form.drivetrain}
                onChange={(e) => set('drivetrain', e.target.value)}
              >
                <option value="">Nincs megadva</option>
                {options(DRIVETRAIN_LABELS).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Teljesítmény (kW)" htmlFor="powerKw">
              <Input
                id="powerKw"
                type="number"
                step="0.1"
                value={form.powerKw}
                onChange={(e) => set('powerKw', e.target.value)}
              />
            </Field>
            <Field label="Teljesítmény (LE)" htmlFor="powerHp">
              <Input
                id="powerHp"
                type="number"
                value={form.powerHp}
                onChange={(e) => set('powerHp', e.target.value)}
              />
            </Field>
            <Field label="Ajtók száma" htmlFor="doors">
              <Input
                id="doors"
                type="number"
                value={form.doors}
                onChange={(e) => set('doors', e.target.value)}
              />
            </Field>
            <Field label="Ülések száma" htmlFor="seats">
              <Input
                id="seats"
                type="number"
                value={form.seats}
                onChange={(e) => set('seats', e.target.value)}
              />
            </Field>
            <Field label="Első forgalomba helyezés" htmlFor="firstRegistration">
              <Input
                id="firstRegistration"
                type="date"
                value={form.firstRegistration}
                onChange={(e) => set('firstRegistration', e.target.value)}
              />
            </Field>
            <Field label="Műszaki vizsga lejárata" htmlFor="motExpiry">
              <Input
                id="motExpiry"
                type="date"
                value={form.motExpiry}
                onChange={(e) => set('motExpiry', e.target.value)}
              />
            </Field>
          </Section>

          {showBattery && (
            <Section title="Elektromos adatok">
              <Field label="Akkumulátor kapacitás (kWh)" htmlFor="batteryCapacityKwh">
                <Input
                  id="batteryCapacityKwh"
                  type="number"
                  step="0.1"
                  value={form.batteryCapacityKwh}
                  onChange={(e) => set('batteryCapacityKwh', e.target.value)}
                />
              </Field>
              <Field label="Hatótávolság (km, WLTP)" htmlFor="rangeKm">
                <Input
                  id="rangeKm"
                  type="number"
                  value={form.rangeKm}
                  onChange={(e) => set('rangeKm', e.target.value)}
                />
              </Field>
              <Field
                label="Akkumulátor állapot (%)"
                htmlFor="batteryHealthPercent"
                hint="Mért SOH érték, ha van róla jegyzőkönyv."
              >
                <Input
                  id="batteryHealthPercent"
                  type="number"
                  value={form.batteryHealthPercent}
                  onChange={(e) => set('batteryHealthPercent', e.target.value)}
                />
              </Field>
              <Field label="Kábelek" htmlFor="cableSet" hint="Pl. „Type 2 + Schuko”.">
                <Input
                  id="cableSet"
                  value={form.cableSet}
                  onChange={(e) => set('cableSet', e.target.value)}
                />
              </Field>
              <Field label="AC töltés (kW)" htmlFor="chargingAcKw">
                <Input
                  id="chargingAcKw"
                  type="number"
                  step="0.1"
                  value={form.chargingAcKw}
                  onChange={(e) => set('chargingAcKw', e.target.value)}
                />
              </Field>
              <Field label="DC töltés (kW)" htmlFor="chargingDcKw">
                <Input
                  id="chargingDcKw"
                  type="number"
                  step="0.1"
                  value={form.chargingDcKw}
                  onChange={(e) => set('chargingDcKw', e.target.value)}
                />
              </Field>
            </Section>
          )}

          <Card className="p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-brand">Leírás</h2>
            <div className="mt-4 space-y-4">
              <Field
                label="Felszereltség"
                htmlFor="equipment"
                hint="Soronként egy tétel. Felsorolásként jelenik meg a hirdetésben."
              >
                <Textarea
                  id="equipment"
                  rows={5}
                  value={form.equipment}
                  onChange={(e) => set('equipment', e.target.value)}
                  placeholder={'Navigáció\nTolatókamera\nÜlésfűtés'}
                />
              </Field>
              <Field label="Leírás (magyar)" htmlFor="descriptionHu" required>
                <Textarea
                  id="descriptionHu"
                  rows={6}
                  value={form.descriptionHu}
                  onChange={(e) => set('descriptionHu', e.target.value)}
                  required
                />
              </Field>
              <Field
                label="Leírás (angol)"
                htmlFor="descriptionEn"
                hint="Nem kötelező. Ha üresen hagyja, az angol oldalon a magyar leírás jelenik meg."
              >
                <Textarea
                  id="descriptionEn"
                  rows={6}
                  value={form.descriptionEn}
                  onChange={(e) => set('descriptionEn', e.target.value)}
                />
              </Field>
            </div>
          </Card>

          <Card className="p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-brand">Állapot</h2>
            <div className="mt-4 max-w-md">
              <Field label="Hirdetés állapota" htmlFor="status" hint={STATUS_HINTS[form.status]}>
                <Select
                  id="status"
                  value={form.status}
                  onChange={(e) => set('status', e.target.value as VehicleStatus)}
                >
                  {options(STATUS_LABELS).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </Card>

          {error && (
            <p role="alert" className="text-sm text-red-400">
              {error}
            </p>
          )}

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Mentés…' : 'Mentés'}
            </Button>
            {saved && <span className="text-sm text-brand">Elmentve.</span>}
            {!isNew && data && (
              <ConfirmButton
                type="button"
                onConfirm={() =>
                  api
                    .delete(`/admin/vehicles/${id}`)
                    .then(() => {
                      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
                      navigate('/vehicles');
                    })
                    .catch((e: ApiError) => setError(e.message))
                }
                confirmText={`Biztosan törli a(z) "${vehicleTitle(data)}" hirdetést? A képei is törlődnek.`}
                className="ml-auto"
              >
                Hirdetés törlése
              </ConfirmButton>
            )}
          </div>
        </form>

        {/* Photos need an id to attach to, so this panel only exists once the
            car has been saved at least once. */}
        {isNew ? (
          <Card className="mt-4 p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-brand">Képek</h2>
            <p className="mt-2 text-sm text-white/50">
              Mentse el a járművet, utána tudja feltölteni a képeket.
            </p>
          </Card>
        ) : (
          data && <VehiclePhotos vehicle={data} />
        )}
      </QueryState>
    </>
  );
}
